import { Inject } from '@odinjs/odin';
import { AutocompleteAPI, FormAPI, ToastService } from '@philips-internal/oswald-ui-components';
import { Placement, ToastLevel, ToastType } from '@philips-internal/oswald-ui-constants';
import { Value } from '@philips-internal/oswald-ui-core';
import Controller, { OnMount, OnSubmit } from '@philips-internal/oswald-ui-core/lib/client/index.js';
import { LanguageService, LocaleService, MessageService } from '@philips-internal/oswald-ui-core/lib/services';
import { foundationContext } from '@philips-internal/oswald-ui-foundation';
import { isDefined } from '@philips-internal/oswald-ui-utils';

import { FragmentNames } from '../../codegen';
import { CustomerUserItemAliases } from '../../codegen/constants/aliases/customer-user-item-aliases';
import { NewCustomerUser, UserCustomerForm } from '../../interfaces/user';
import { UserService } from '../../services';

@Controller({fragment: FragmentNames.CUSTOMER_USER_ITEM})
class CustomerUserItemController {

  @Inject(CustomerUserItemAliases.FORM_USER)
  formUser !: FormAPI;

  @Inject(CustomerUserItemAliases.AUTOCOMPLETE_LANGUAGE)
  autocompleteLanguage!: AutocompleteAPI;

  @Inject(CustomerUserItemAliases.AUTOCOMPLETE_LOCALE)
  autocompleteLocale!: AutocompleteAPI;

  private languages = this.getLanguages();
  private locales = this.getLocales();

  @OnSubmit(CustomerUserItemAliases.FORM_USER)
  onSubmitForm(): void{
    
    const record = this.formUser.getRecord();
    const form = record as UserCustomerForm;
    const user: NewCustomerUser = {
      userName: form.name,
      login: form.login,
      email: form.email,
      emailConfirmation: form.emailConfirmation,
      language: form.language.value,
      locale: form.locale.value,
      customerId: parseInt( foundationContext.getEstablishment()?.id ?? ''),
      status: true,
    };

    void UserService.create(user).then( (response: NewCustomerUser) => {
      this.showSuccessMessage(form.name || response.userName, 'create.success');
    }).catch(error =>{
      this.showErrorMessage(error, form.name);
    });
  }

  @OnMount(CustomerUserItemAliases.AUTOCOMPLETE_LANGUAGE)
  onMountAutocompleteLanguage(): void {
    this.autocompleteLanguage.setData(this.languages);
  }

  @OnMount(CustomerUserItemAliases.AUTOCOMPLETE_LOCALE)
  onMountAutocompleteLocale(): void {
    this.autocompleteLocale.setData(this.locales);
  }

  private showSuccessMessage(userName: string, message: string): void {
    ToastService.open({
      dismissible: false,
      id: 'update-success',
      message,
      placement: Placement.TOP,
      showIcon: true,
      level: ToastLevel.SUCCESS,
      title: userName,
      type: ToastType.BUBBLE,
    });
    this.formUser.setEditing(false);
    this.formUser.setLocked(true);
    this.formUser.setLoading(false);
  }

  showErrorMessage = (error: any, profileName: string): void => {
    this.formUser.setLoading(false);
    this.formUser.setEditing(true);
    let message: string;
    if (error.status === 404) {
      message = MessageService.translate('user.error-not-found');
    } 
    if(error.status === 409){
      message = MessageService.translate('user.already-exists');
    } else {
      message = MessageService.translate('user.error-unexpected-create');
    }
    if (isDefined(error.body.errorCode) && error.body.errorCode > 0) {
      if (error.body.errorCode === 1200) {
        message += MessageService.translate('user.already-exists');
      }
      if (error.body.errorCode === 1205) {
        message += MessageService.translate('user.password-weak');
      }
    }

    ToastService.open({
      dismissible: false,
      id: 'create-error',
      message,
      placement: Placement.TOP,
      showIcon: true,
      level: ToastLevel.ERROR,
      title: profileName,
      type: ToastType.BUBBLE,
    });
  };

  private getLanguages(): Record<string, Value>[] {
    const languages = LanguageService.getSupportedLanguages().filter(language => language === 'pt' || language === 'en' || language === 'es');
    return languages.map(language => ({
      label: MessageService.translate(`language.${language}`),
      value: language,
    }));
  }

  private getLocales(): Record<string, Value>[] {
    const locales = LocaleService.getSupportedLocales();
    const data: Record<string, Value>[] = [];
    if (locales.length === 0) {
      const locale = LocaleService.getCurrentLocale();
      data.push({
        label: MessageService.translate(`language.${locale}`),
        value: locale,
      });
    } else {
      locales.forEach(locale => data.push({
        label: MessageService.translate(`language.${locale}`),
        value: locale,
      }));
    }
    return data;
  }

}
export default CustomerUserItemController;
